import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { useAuthStore, useCollectionStore, useMemoryStore, useUIStore } from '@/store';
import { accountApi } from '@/lib/api';
import { useEarnProgramsRefreshStore } from '@/store/earnProgramsRefresh';
import { trackEvent } from '@/constants/utils/analytics';

// Open the actual feature flow from either checklist surface.
const CHECKLIST_ACTIONS = {
  open_image: ({ router }) => {
    const { useChatInputStore } = require('@/store');
    const { openHomeComposer } = require('@/lib/openHomeComposer');
    const composer = useChatInputStore.getState();
    composer.clearPendingAutoSend();
    composer.initializeNewChatWithFiles([], 'Create an image of a cozy cabin in the mountains at sunrise.');
    openHomeComposer(router, { work: false });
  },
  upload_file: ({ openModal, router }) => {
    const userId = useAuthStore.getState().user?.id;
    openModal('FileUploadModal', {
      libraryUpload: true,
      onUpload: (result) => {
        if (useAuthStore.getState().user?.id !== userId) return;
        const fileIds = (result?.results || [])
          .filter(file => file.status === 'success' && file.uploadedFile?.id)
          .map(file => file.uploadedFile.id);
        if (!fileIds.length) return;
        const { useChatInputStore } = require('@/store');
        const { openHomeComposer } = require('@/lib/openHomeComposer');
        const composer = useChatInputStore.getState();
        composer.clearPendingAutoSend();
        composer.initializeNewChatWithFiles(fileIds, 'Summarize this file and highlight the most useful takeaways.');
        openHomeComposer(router, { work: false });
        useAuthStore.getState().fetchUser?.().catch(() => {});
      },
    });
  },
  open_memory: ({ openModal, isXS }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android' || isXS) {
      openModal('SettingsModalMobile', { initialScreen: 'memory' });
    } else {
      openModal('SettingsModalWeb', { tab: 'memory' });
    }
  },
  open_collection: ({ openModal }) => openModal('CreateCollectionModal'),
  start_voice: () => {
    const { requirePlusForVoice } = require('@/lib/requirePlusForVoice');
    if (!requirePlusForVoice()) return;
    const { useVoiceModeStore } = require('@/store');
    useVoiceModeStore.getState().actions.startVoiceMode(null);
  },
  open_import: ({ openModal, isXS }) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android' || isXS) {
      openModal('SettingsModalMobile', { initialScreen: 'import' });
    } else {
      openModal('SettingsModalWeb', { tab: 'import' });
    }
  },
};

export function runChecklistAction(slug, deps) {
  const handler = CHECKLIST_ACTIONS[slug];
  if (handler) handler(deps);
}

// Matomo name = item id or plus/free; value = 1 if Plus, 0 if free.
export function trackChecklistEvent(action, name) {
  const isPlus = !!useAuthStore.getState().user?.flags?.isPlus;
  trackEvent('checklist', action, name ?? (isPlus ? 'plus' : 'free'), isPlus ? 1 : 0);
}

// A short feature tour, with completion based on real use. Free accounts can
// use every core item; image generation uses their available credits. Only
// Plus accounts see voice and the existing earn-program credit amounts.
export const CHECKLIST_ITEMS = [
  {
    id: 'send_message',
    icon: 'lightbulb',
    label: 'Try a chat idea',
    hint: 'Writing, learning, and everyday help',
    route: '/ideas',
    credits: 0,
    check: (u) => (u?.stats?.total_sent_message_all_count || 0) > 0,
  },
  {
    id: 'create_image',
    icon: 'image',
    label: 'Create an image',
    hint: 'Describe a picture or try an example',
    action: 'open_image',
    credits: 10,
    check: (u) => (u?.stats?.total_sent_message_image_count || 0) > 0,
  },
  {
    id: 'upload_file',
    icon: 'file-arrow-up',
    label: 'Ask about a file',
    hint: 'Upload a PDF, image, or document',
    action: 'upload_file',
    credits: 10,
    check: (u) => (u?.tracking?.cachedFileCount || 0) > 0,
  },
  {
    id: 'add_memory',
    icon: 'brain',
    label: 'Personalize your AI',
    hint: 'Save something you want it to remember',
    action: 'open_memory',
    credits: 20,
    check: (u, { memoryCount = 0 } = {}) => memoryCount > 0 || (u?.tracking?.cachedMemoryCount || 0) > 0,
  },
  {
    id: 'create_collection',
    icon: 'folder-plus',
    label: 'Create a collection',
    hint: 'Keep related chats and files together',
    action: 'open_collection',
    credits: 0,
    check: (_u, { collectionCount = 0 } = {}) => collectionCount > 0,
  },
  {
    id: 'import_chats',
    icon: 'file-import',
    label: 'Bring your chats with you',
    hint: 'Import from ChatGPT, Claude, or Grok',
    action: 'open_import',
    credits: 200,
    check: (u) => (u?.tracking?.cachedImportedConversationCount || 0) > 0,
  },
  {
    id: 'try_voice',
    icon: 'microphone',
    label: 'Try voice mode',
    hint: 'Start a hands-free conversation',
    action: 'start_voice',
    plusOnly: true,
    credits: 30,
    check: (u) => (u?.tracking?.cachedVoiceSessionCount || 0) > 0,
  },
];

export const getChecklistItems = (user) => CHECKLIST_ITEMS.filter(item => !item.plusOnly || user?.flags?.isPlus);

const DAILY_CREDITS = 10;
const CHECKLIST_MAX_ACCOUNT_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export function useChecklist() {
  const user = useAuthStore(state => state.user);
  const fetchUserCredits = useAuthStore(state => state.fetchUserCredits);
  const fetchEarnPrograms = useEarnProgramsRefreshStore((s) => s.fetchEarnPrograms);

  const items = useMemo(() => getChecklistItems(user), [user]);
  const collectionCount = useCollectionStore(state => state.collectionIds.length);
  const memoryCount = useMemoryStore(state => state.memoryIds.length);
  // An unset preference is expanded, including on a shared device's new account.
  const sectionKey = `get-started:${user?.id}`;
  const collapsed = useUIStore(state => state.collapsedSections?.[sectionKey] === true);
  const toggleSection = useUIStore(state => state.toggleSection);
  const toggleCollapsed = useCallback(() => toggleSection(sectionKey), [toggleSection, sectionKey]);
  const dismissed = useUIStore(state => state.dismissedChecklistsByUser?.[user?.id] === true);
  const dismissChecklist = useUIStore(state => state.dismissChecklist);
  const dismiss = useCallback(() => {
    if (!user?.id) return;
    dismissChecklist(user.id);
    trackChecklistEvent('dismissed');
  }, [dismissChecklist, user?.id]);

  const completionMap = useMemo(() => {
    const map = {};
    for (const item of items) {
      map[item.id] = item.check(user, { collectionCount, memoryCount });
    }
    return map;
  }, [user, items, collectionCount, memoryCount]);

  const completedCount = useMemo(
    () => items.filter(i => completionMap[i.id]).length,
    [items, completionMap],
  );

  const earnableCredits = useMemo(() => {
    if (!user?.flags?.isPlus) return 0;
    return items.filter(i => !completionMap[i.id] && i.credits > 0)
      .reduce((sum, i) => sum + i.credits, 0);
  }, [items, completionMap, user?.flags?.isPlus]);

  const sortedItems = useMemo(() => {
    const incomplete = items.filter(i => !completionMap[i.id]);
    const completed = items.filter(i => completionMap[i.id]);
    return [...incomplete, ...completed];
  }, [items, completionMap]);

  const total = items.length;
  const isComplete = completedCount >= total;

  const hasUser = Boolean(user?.id);
  const isGhost = !!user?.flags?.isGhost;
  const isPlus = !!user?.flags?.isPlus;
  const showCredits = hasUser && isPlus;
  // Only pin these prompts during the first month. Missing or invalid signup
  // dates stay hidden; earning credits in Settings has no account-age cutoff.
  const createdAt = user?.createdAt ? new Date(user.createdAt).getTime() : NaN;
  const accountAge = Date.now() - createdAt;
  const canShowPinnedChecklist = hasUser && !isGhost && !dismissed
    && accountAge >= 0 && accountAge <= CHECKLIST_MAX_ACCOUNT_AGE_MS;
  const shouldShow = canShowPinnedChecklist && !isComplete;
  const shouldShowDaily = canShowPinnedChecklist && showCredits;

  // --- Daily claim state (Plus users only) ---
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyAvailable, setDailyAvailable] = useState(false);
  const [dailyClaiming, setDailyClaiming] = useState(false);
  const [dailyJustClaimed, setDailyJustClaimed] = useState(false);
  const dailyCheckedRef = useRef(false);

  useEffect(() => {
    if (!shouldShowDaily || dailyCheckedRef.current) return;
    dailyCheckedRef.current = true;
    setDailyLoading(true);

    fetchEarnPrograms()
      .then((programs) => {
        const daily = programs.find(p => p.key === 'daily_claim');
        setDailyAvailable(daily?.isAvailable ?? false);
      })
      .catch(() => {})
      .finally(() => setDailyLoading(false));
  }, [shouldShowDaily, fetchEarnPrograms]);

  const claimDaily = useCallback(async () => {
    if (dailyClaiming || !dailyAvailable) return false;
    try {
      setDailyClaiming(true);
      await accountApi.claimDailyCredits();
      setDailyAvailable(false);
      setDailyJustClaimed(true);
      fetchUserCredits?.();
      return true;
    } catch {
      return false;
    } finally {
      setDailyClaiming(false);
    }
  }, [dailyClaiming, dailyAvailable, fetchUserCredits]);

  return {
    dismiss,
    collapsed,
    toggleCollapsed,
    completionMap,
    completedCount,
    earnableCredits,
    sortedItems,
    total,
    isComplete,
    shouldShow,
    shouldShowDaily,
    showCredits,
    isPlus,
    dailyLoading,
    dailyAvailable,
    dailyClaiming,
    dailyJustClaimed,
    dailyCredits: DAILY_CREDITS,
    claimDaily,
  };
}
